import { map, pick, reduce, isEmpty, filter, each, isString, difference, pluck, keys } from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { defaultBid } from '@bingads-webui-campaign-legacy/budget/shared/defaultBid';

// TODO define the type of dsaTargets here
export const DSA_TARGET_TYPES = {
  categoryTargets: 'categoryTargets',
  allTargets: 'allTargets',
  specificTargets: 'specificTargets',
  customLabelTargets: 'customLabelTargets',
};

export const DSA_TARGET_SUB_TYPES = {
  exactUrls: 'exactUrls',
  rules: 'rules',
};

export const DSA_TARGET_OPERAND_TYPES = {
  Url: 'Url',
  Category: 'Category',
  PageTitle: 'PageTitle',
  PageContent: 'PageContent',
  CustomLabel: 'CustomLabel',
};

export const DSA_TARGET_OPERAND_TYPE_STRINGS = {
  [DSA_TARGET_OPERAND_TYPES.Url]: _TL_('URL'),
  [DSA_TARGET_OPERAND_TYPES.Category]: _TL_('Category'),
  [DSA_TARGET_OPERAND_TYPES.PageTitle]: _TL_('Page title'),
  [DSA_TARGET_OPERAND_TYPES.PageContent]: _TL_('Page content'),
  [DSA_TARGET_OPERAND_TYPES.CustomLabel]: _TL_('Custom label'),
};

export const DSA_TARGET_OPERATOR_TYPES = {
  Contains: 'Contains',
  Equals: 'Equals',
};

export const DSA_TARGET_OPERATOR_TYPE_STRINGS = {
  [DSA_TARGET_OPERATOR_TYPES.Contains]: _TL_('Contains'),
  [DSA_TARGET_OPERATOR_TYPES.Equals]: _TL_('Equals'),
};

export const DSA_SPECIFIC_TARGET_OPERATOR_MAP = {
  [DSA_TARGET_OPERAND_TYPES.Url]: 'Contains',
  [DSA_TARGET_OPERAND_TYPES.PageTitle]: 'Contains',
  [DSA_TARGET_OPERAND_TYPES.PageContent]: 'Contains',
  [DSA_TARGET_OPERAND_TYPES.Category]: 'Equals',
  [DSA_TARGET_OPERAND_TYPES.CustomLabel]: 'Equals',
};

const formatConditions = conditions => map(conditions, condition => pick(condition, 'Operand', 'Operator', 'ArgumentOperand'));

const convertItemsToDsaTarget = (items, savedItems, type, campaignName, adGroupName) => {
  const itemsToSave = [];

  each(items, (item) => {
    const ItemSaved = savedItems[item.key];

    if (!ItemSaved || ItemSaved.Bid !== item.Bid) {
      let newItem = cloneDeep(item);
      if (ItemSaved) {
        newItem = { ...newItem, conditions: [] };
        newItem.Id = ItemSaved.Id;
      }
      itemsToSave.push(newItem);
    }
  });

  const keyOfItemsToDelete = difference(keys(savedItems), pluck(items, 'key'));

  each(keyOfItemsToDelete, (key) => {
    const ItemSaved = savedItems[key];

    ItemSaved.Status = 'Deleted';
    ItemSaved.conditions = [];
    itemsToSave.push(ItemSaved);
  });

  const dsaTarget = map(itemsToSave, (item) => {
    const res = {
      AdGroupName: adGroupName,
      CampaignName: campaignName,
      Bid: item.Bid || defaultBid(),
      Criterion: {
        Type: 'Webpage',
        WebPage: {
          Parameter: {
            Conditions: type === DSA_TARGET_TYPES.allTargets ? [] : filter(formatConditions(item.conditions), con => !isEmpty(con)),
          },
        },
      },
    };

    const itemStatus = item.Status ? { Status: item.Status } : {};
    const itemId = item.Id ? { Id: item.Id } : {};
    // if (type === 'specificpage' || type === 'exacturls') {
    //   res.serializedConditions = JSON.stringify('conditionsWithOperator');
    // } else {
    //   res.serializedConditions = '';
    // }

    return {
      ...res,
      ...itemStatus,
      ...itemId,
    };
  });

  return dsaTarget;
};

export const viewModelToBulkModelForDsaTarget = ({ dsaViewModel, campaignName, adGroupName }) => {
  const dsaTargets = reduce(dsaViewModel, (acc, value, key) => {
    const items = key === DSA_TARGET_TYPES.specificTargets ? [...value.exactUrls.items, ...value.rules.items] : value.items;
    const savedItems = key === DSA_TARGET_TYPES.specificTargets ? { ...value.exactUrls.savedItems, ...value.rules.savedItems } : value.savedItems;
    const selectedItems = !value.disabled && value.selected ? items : [];
    return ([
      ...acc,
      ...convertItemsToDsaTarget(selectedItems, savedItems, key, campaignName, adGroupName),
    ]);
  }, []);

  return dsaTargets;
};

const dsaTargetsMapper = (conditions) => {
  if (conditions.length === 0) {
    return { dsaType: DSA_TARGET_TYPES.allTargets, viewConditions: [] };
  }
  if (conditions.length > 1) {
    return { dsaType: DSA_TARGET_TYPES.specificTargets, dsaSubtype: DSA_TARGET_SUB_TYPES.rules, viewConditions: [...conditions] };
  }

  const { Operand, Operator } = conditions[0];
  if (Operand === DSA_TARGET_OPERAND_TYPES.CustomLabel) {
    return { dsaType: DSA_TARGET_TYPES.customLabelTargets, viewConditions: [{ ...conditions[0] }] };
  }
  if (Operand === DSA_TARGET_OPERAND_TYPES.Url && Operator === DSA_TARGET_OPERATOR_TYPES.Equals) {
    return { dsaType: DSA_TARGET_TYPES.specificTargets, dsaSubtype: DSA_TARGET_SUB_TYPES.exactUrls, viewConditions: [{ ...conditions[0] }] };
  }
  if (Operand === DSA_TARGET_OPERAND_TYPES.Category) {
    return { dsaType: DSA_TARGET_TYPES.categoryTargets, viewConditions: [{ ...conditions[0] }] };
  }
  return { dsaType: DSA_TARGET_TYPES.specificTargets, dsaSubtype: DSA_TARGET_SUB_TYPES.rules, viewConditions: [...conditions] };
};

const defaultDsaTarget = {
  [DSA_TARGET_TYPES.categoryTargets]: {
    disabled: false,
    selected: false,
    items: [],
    savedItems: {},
  },
  [DSA_TARGET_TYPES.allTargets]: {
    disabled: false,
    selected: false,
    items: [],
    savedItems: {},
  },
  [DSA_TARGET_TYPES.specificTargets]: {
    disabled: false,
    selected: false,
    [DSA_TARGET_SUB_TYPES.exactUrls]: {
      items: [],
      savedItems: {},
    },
    [DSA_TARGET_SUB_TYPES.rules]: {
      items: [],
      savedItems: {},
    },
  },
  [DSA_TARGET_TYPES.customLabelTargets]: {
    disabled: false,
    selected: false,
    items: [],
    savedItems: {},
  },
};

export const bulkModelToViewModelForDsaTarget = (dsaBulkModel, originaladGroupModel) => {
  const res = {
    [DSA_TARGET_TYPES.categoryTargets]: {
      disabled: originaladGroupModel.DsaTargets[DSA_TARGET_TYPES.categoryTargets].disabled,
      selected: false,
      items: [],
      savedItems: {},
    },
    [DSA_TARGET_TYPES.allTargets]: {
      disabled: originaladGroupModel.DsaTargets[DSA_TARGET_TYPES.allTargets].disabled,
      selected: false,
      items: [],
      savedItems: {},
    },
    [DSA_TARGET_TYPES.specificTargets]: {
      disabled: originaladGroupModel.DsaTargets[DSA_TARGET_TYPES.specificTargets].disabled,
      selected: false,
      [DSA_TARGET_SUB_TYPES.exactUrls]: {
        items: [],
        savedItems: {},
      },
      [DSA_TARGET_SUB_TYPES.rules]: {
        items: [],
        savedItems: {},
      },
    },
    [DSA_TARGET_TYPES.customLabelTargets]: {
      disabled: originaladGroupModel.DsaTargets[DSA_TARGET_TYPES.customLabelTargets].disabled,
      selected: false,
      items: [],
      savedItems: {},
    },
  };

  each(dsaBulkModel, (bulkItem) => {
    const {
      Bid, 'Client Id': ClientId, Id, Criterion: { WebPage: { Parameter: { Conditions } } },
    } = bulkItem;
    const { dsaType, dsaSubtype, viewConditions } = dsaTargetsMapper(Conditions);
    if (dsaSubtype) {
      res[dsaType][dsaSubtype].items.push({
        Bid, conditions: viewConditions, 'Client Id': ClientId, Id, isValid: true, isBidValid: true, isConditionValid: true,
      });
    } else {
      res[dsaType].items.push({
        Bid, conditions: viewConditions, 'Client Id': ClientId, Id, isValid: true, isBidValid: true, isConditionValid: true,
      });
    }
    res[dsaType].selected = true;
  });

  return res;
};

export const bulkModelToViewModelForDsaTargetUnderDsaEntity = (dsaBulkModel) => {
  const res = cloneDeep(defaultDsaTarget);

  each(dsaBulkModel, (bulkItem) => {
    const { CriterionBid: { Bid: { Amount } }, Criterion: { Parameter: { Conditions } } } = bulkItem;
    const { dsaType, dsaSubtype, viewConditions } = dsaTargetsMapper(Conditions);
    if (dsaSubtype) {
      res[dsaType][dsaSubtype].items.push({ Bid: Amount, conditions: viewConditions });
    } else {
      res[dsaType].items.push({ Bid: Amount, conditions: viewConditions });
    }
    res[dsaType].selected = true;
  });

  return res;
};

const checkArgumentOperandFormat = (argumentOperand) => {
  if (!isString(argumentOperand)) {
    return argumentOperand;
  }
  let hasComma = false;
  let hasDoubleQuote = false;
  let resultArgumentOperand = argumentOperand;

  if (argumentOperand.includes(',')) {
    hasComma = true;
  }
  if (argumentOperand.includes('"')) {
    hasDoubleQuote = true;
  }
  if (hasDoubleQuote) {
    resultArgumentOperand = resultArgumentOperand.replaceAll('"', '""');
  }
  if (hasDoubleQuote || hasComma) {
    resultArgumentOperand = `"${resultArgumentOperand}"`;
  }
  return resultArgumentOperand;
};

export const generateKeyAndFormateDsaTarget = (conditions) => {
  const conditionsWithoutDisplayValue = [];

  each(conditions, (condition) => {
    const formattedCondition = {
      ...condition,
      ArgumentOperand: checkArgumentOperandFormat(condition.ArgumentOperand),
    };
    conditionsWithoutDisplayValue.push(pick(formattedCondition, 'ArgumentOperand', 'Operand', 'Operator'));
  });

  const key = JSON.stringify(conditionsWithoutDisplayValue);

  return {
    isValid: true,
    key,
    conditions,
  };
};

export const getNestedValue = (obj, path) => path.split('.').reduce((acc, key) => acc && acc[key], obj);

export const insertErrorsByClientIdAndGenerateKey = (data, paths, errors) => {
  const errorClientIdMap = {};

  errors.forEach((error) => {
    const { clientId, message } = error;
    errorClientIdMap[clientId] = message;
  });

  paths.forEach((path) => {
    const value = getNestedValue(data, path);

    const savedItems = {};

    if (value && value.items) {
      value.items.forEach((item) => {
        const newConditions = generateKeyAndFormateDsaTarget(item.conditions);
        if (errorClientIdMap[item['Client Id']]) {
          const newItem = { ...item, errors: item.errors || [] };
          Object.assign(item, newItem);
          item.errors.push({ message: errorClientIdMap[item['Client Id']] });
        } else {
          savedItems[newConditions.key] = item;
        }
        Object.assign(item, newConditions);
      });
    }
    Object.assign(value, { savedItems });
  });
};
